import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { ScreenSizeDetectorService, UiKitModule } from '@serviceplan/ui-kit';
import { AssetBasketService, DownloadAssetsService } from '@thehubz/client/asset-services';
import {
  AppSettings,
  AssetUIModel,
  CanPipe,
  ClientCoreModule,
  ClientEnvironment,
  ClientNotificationHandlerService,
  ENVIRONMENT,
  NotificationService,
  SelectionAsset,
  StorageUtilityLib,
  accessGlobal,
  anyAsset,
  anyGlobal,
  anyReport,
} from '@thehubz/client/core';
import { Asset, AssetsApiService } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType } from '@thehubz/shared/model';
import { lastValueFrom } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AssetCommonService } from '../../services/asset-common.service';
import { CollectionStepperDialogService } from '../../services/collection-stepper-dialog.service';
import { SharingStepperDialogService } from '../../services/sharing-stepper-dialog.service';
import { AssetDeletionDialogComponent } from '../asset-detail-view/asset-deletion-dialog/asset-deletion-dialog.component';
import { AssetDownloadComponent, AssetDownloadData } from '../asset-download/asset-download.component';
import { BulkExportDownloadsComponent } from './bulk-export-downloads/bulk-export-downloads.component';

@Component({
  selector: 'thehubz-asset-bulk-edit',
  templateUrl: './asset-bulk-edit.component.html',
  styleUrls: ['./asset-bulk-edit.component.scss'],
  standalone: true,
  imports: [CommonModule, UiKitModule, ClientCoreModule],
})
export class AssetBulkEditComponent implements OnInit {
  @Input() buttonText!: string;
  @Input() bulkEditType!: string;
  @Input() filteredAssets?: AssetUIModel[];
  @Input() showEmptyState = false;
  @Input() assetCount!: number;
  @Input() collectionId?: string;
  @Input() showEditButton? = true;
  @Input() showSelectAllCheckbox? = true;
  @Input() collectionPublished?: boolean = false;
  @Input() collectionOwner = false;
  @Output() toggleSelectAllCheckbox = new EventEmitter<AnyType>();
  @Output() shareSelectedAssets = new EventEmitter<AnyType>();
  @Output() clickClearBasket = new EventEmitter<AnyType>();
  @Output() clickRemoveFromCollection = new EventEmitter<AnyType>();
  @Output() unselectAll = new EventEmitter<AnyType>();
  @Output() editAsset = new EventEmitter<AnyType>();
  @Output() downloadCollection = new EventEmitter<AnyType>();
  @Output() addToDashboard = new EventEmitter<AnyType>();
  @Output() deleteCollection = new EventEmitter<AnyType>();
  @Output() shareCollection = new EventEmitter<AnyType>();
  @Output() publishCollection = new EventEmitter<AnyType>();
  @Output() unpublishCollection = new EventEmitter<AnyType>();
  @Output() updateAssetDraft = new EventEmitter<AnyType>();
  @Output() showAssetMismatchDialog = new EventEmitter<AnyType>();

  private itemsSelected: AnyType = [
    { type: 'assets', items: [] },
    { type: 'collection', items: [] },
    { type: 'collections', items: [] },
  ];
  private currentTypeIndex!: number;
  allDraftAssets = false;
  mixedAssetStatus = false;
  readonly CustomClients = CustomClients;
  readonly brandingKey!: CustomClients;
  readonly max500Tooltip = $localize`Maximum 500 assets can be selected`;

  readonly AnyAsset = anyAsset;
  readonly Globally = anyGlobal;
  readonly AnyReport = anyReport;
  readonly AccessGlobal = accessGlobal;

  @ViewChild('bulkMenuTrigger') trigger!: MatMenuTrigger;

  constructor(
    @Inject(ENVIRONMENT) env: ClientEnvironment,
    private canPipe: CanPipe,
    private dialog: MatDialog,
    private assetService: AssetsApiService,
    private storageUtility: StorageUtilityLib,
    private assetBasketService: AssetBasketService,
    private assetCommonService: AssetCommonService,
    private notificationsService: NotificationService,
    private downloadAssetsService: DownloadAssetsService,
    private sharingStepperDialogService: SharingStepperDialogService,
    protected collectionStepperDialogService: CollectionStepperDialogService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
    protected screenSizeDetectorService: ScreenSizeDetectorService,
  ) {
    this.brandingKey = env.brandingKey ?? CustomClients.Standard;
  }

  ngOnInit(): void {
    this.currentTypeIndex = this.itemsSelected.findIndex((obj: { type: string }) => obj.type === this.bulkEditType);
    if (!sessionStorage.getItem(AppSettings.SESSION_STORAGE_KEY)) {
      this.storageUtility.setSessionStorage(AppSettings.SESSION_STORAGE_KEY, this.itemsSelected);
    }
  }

  async downloadAssets() {
    let assetIDs: string[] = [];
    if (this.bulkEditType === 'assets' || this.bulkEditType === 'collection') {
      assetIDs = this.getItemsSelectedData('items').map((asset) => asset._id);
    } else {
      assetIDs = this.filteredAssets?.map((asset) => asset._id) ?? [];
    }

    const downloadData = this.downloadAssetsService.createDownloadData(assetIDs);
    let downloadType = $localize`Collection Download`;
    if (this.bulkEditType !== 'collection') {
      downloadType = assetIDs.length > 1 ? $localize`Assets Download` : $localize`Asset Download`;
    }
    this.dialog.open<AssetDownloadComponent, AssetDownloadData>(AssetDownloadComponent, {
      width: '100%',
      maxWidth: '70rem',
      height: '100%',
      maxHeight: '50rem',
      disableClose: true,
      data: { data: downloadData, downloadType, collectionId: this.collectionId },
    });
  }

  editAssets(editType: string) {
    const selectedAssets = this.getItemsSelectedData('items');
    const assetObject = {
      editType: editType,
      assets: selectedAssets,
    };
    this.editAsset.emit(assetObject);
  }

  updateAssetOnDraft() {
    const selectedAssets = this.getItemsSelectedData('items');
    const assetObject = {
      assets: selectedAssets,
    };
    this.updateAssetDraft.emit(assetObject);
  }

  removeFromCollection() {
    const selectedAssets = this.getItemsSelectedData('items');
    this.clickRemoveFromCollection.emit(selectedAssets);
  }

  async openMenu(event: AnyType) {
    event.preventDefault();
    this.allDraftAssets = false;
    this.mixedAssetStatus = false;
    const selectedAssets = await this.asAssets(this.getItemsSelectedData('items'));
    let allDraftFlag = true;
    let noDraftFlag = true;
    if (selectedAssets?.length > 0) {
      selectedAssets.forEach((asset) => {
        if (asset.status === Asset.StatusEnum.Draft) {
          noDraftFlag = false;
        } else {
          allDraftFlag = false;
        }
      });
    }
    if (allDraftFlag) {
      this.allDraftAssets = true;
    }
    if (!noDraftFlag && !allDraftFlag) {
      this.mixedAssetStatus = true;
    }
    if (!this.mixedAssetStatus) {
      this.trigger.openMenu();
    } else {
      this.trigger.closeMenu();
      this.showAssetMismatchDialog.emit();
    }
  }

  deleteSelectedAssets(planDeletionFlag: boolean) {
    this.deleteAssets(planDeletionFlag).then((data) => {
      if (data.actionType === 'deleteAsset') {
        this.unselectAll.emit();
        data.assetIDs.forEach((id: string) => {
          this.assetBasketService.removeFromBasket(id);
        });
        this.updateSelectedItems('assets', this.getItemsSelectedData('items'));
      }
    });
  }

  private async deleteAssets(planDeletionFlag: boolean): Promise<AnyType> {
    const assets = await this.getAssets();
    return new Promise((resolve, reject) => {
      const dialogRef = this.dialog.open(AssetDeletionDialogComponent, {
        data: { bulkEdit: assets.length > 1 ? true : false, planDeletionFlag: planDeletionFlag },
      });
      dialogRef.disableClose = true;
      dialogRef
        .afterClosed()
        .pipe(
          tap((data) => {
            if (data?.confirm) {
              switch (data.actionType) {
                case 'planDeletion': {
                  const assetIds = assets.map((asset) => asset._id);
                  this.assetService.assetsControllerUpdateMany({ ids: assetIds, updateData: data.plannedDeletionObj }).subscribe({
                    next: () => {
                      this.clientNotificationHandlerService.successNotification($localize`Assets planned for deletion successfully`);
                      assets.forEach((asset) => {
                        this.assetCommonService.assetPlannedDeletion$.next({ _id: asset._id, plannedDeletion: data.deletionDate } as AssetUIModel);
                      });
                      resolve({ assetIDs: assetIds, actionType: data.actionType });
                    },
                    error: (err) => {
                      this.clientNotificationHandlerService.errorNotification(err);
                      reject(err);
                    },
                  });
                  break;
                }
                case 'deleteAsset': {
                  const assetIds = assets.map((asset) => asset._id);
                  this.assetService.assetsControllerRemoveMultiple(true, { assetIds }).subscribe({
                    next: (res) => {
                      const deletedAssetIDs = res.details.map(function (asset: Record<string, AnyType>) {
                        if (asset['status'] == 'success') {
                          return asset['id'];
                        }
                      });
                      if (res.status === 'success') {
                        this.clientNotificationHandlerService.successNotification($localize`Assets deleted successfully`);
                        resolve({ assetIDs: deletedAssetIDs, actionType: data.actionType });
                      } else {
                        this.notificationsService.addNotification({
                          type: 'failure',
                          message: res.details.length ? res.details[0].status : $localize`Something went wrong`,
                        });
                        resolve({ assetIDs: deletedAssetIDs, actionType: data.actionType });
                      }
                    },
                    error: (err) => {
                      this.clientNotificationHandlerService.errorNotification(err);
                      reject(err);
                    },
                  });
                  break;
                }
              }
            }
          }),
        )
        .subscribe();
    });
  }

  getItemsSelectedData(dataType: string): SelectionAsset[] {
    if (this.bulkEditType === 'collection') {
      const basketIDs = this.assetBasketService.assetIdsFromMyBasket;
      return basketIDs.length ? basketIDs.map((id) => ({ _id: id })) : [];
    } else {
      const itemsSelected = JSON.parse(sessionStorage[AppSettings.SESSION_STORAGE_KEY]);
      if (this.bulkEditType === 'basket') this.currentTypeIndex = 0;
      return itemsSelected[this.currentTypeIndex]?.[dataType] ?? [];
    }
  }

  private async getAssets(): Promise<Asset[]> {
    if (this.bulkEditType === 'assets' || this.bulkEditType === 'collection') {
      return this.asAssets(this.getItemsSelectedData('items'));
    } else {
      return this.filteredAssets ?? [];
    }
  }

  private getAssetIds(): string[] {
    if (this.bulkEditType === 'assets' || this.bulkEditType === 'collection') {
      return this.getItemsSelectedData('items').map((asset) => asset._id);
    } else {
      return this.filteredAssets?.map((asset) => asset._id) ?? [];
    }
  }

  private async asAssets(assets: SelectionAsset[]): Promise<Asset[]> {
    const foundAssets: Asset[] = [];
    const assetsToLoad: string[] = [];
    assets.forEach((selectAsset) => {
      const asset = this.filteredAssets?.find((filteredAsset) => filteredAsset._id === selectAsset._id);
      if (asset) {
        foundAssets.push(asset);
      } else {
        assetsToLoad.push(selectAsset._id);
      }
    });

    let loaded: Asset[] = [];
    if (assetsToLoad.length > 0) {
      loaded = (await lastValueFrom(this.assetService.assetsControllerFindMany({ ids: assetsToLoad }))).result;
    }
    return foundAssets.concat(loaded);
  }

  async addToCollection() {
    const selectedAssets = await this.getAssets();
    this.collectionStepperDialogService.openDialogForCollection(selectedAssets).then((result) => {
      if (result) {
        this.updateSelectedItems('assets', selectedAssets);
      }
    });
  }

  clickUnselectAll() {
    const selectedAssets = this.getItemsSelectedData('items');
    const assetIDs = selectedAssets.map((asset) => asset._id);
    assetIDs.forEach((id: string) => {
      this.assetBasketService.removeFromBasket(id);
    });
    this.updateSelectedItems('assets', selectedAssets);
    this.unselectAll.emit();
  }

  shareAssets() {
    const assetIDs = this.getAssetIds();
    this.assetService.assetsControllerFindMany({ ids: assetIDs, optFor: 'share' }).subscribe({
      next: async (asset) => {
        const assets = asset.result.map((asset) => ({
          assetId: asset._id,
          name: asset.name,
          version: asset.currentVersion?.version || 0,
          aspectRatios: asset.currentVersion?.aspectRatios || [],
        }));
        const stepperData = await this.sharingStepperDialogService.createStepperModel(
          assets,
          $localize`Share assets: <span class="font-light">Selected assets<span>`,
        );
        this.sharingStepperDialogService.openSharingDialog(stepperData);
      },
      error: (err) => {
        this.clientNotificationHandlerService.errorNotification(err);
      },
    });
  }

  private updateSelectedItems(type: string, assets: AnyType) {
    const selectedItems = JSON.parse(sessionStorage[AppSettings.SESSION_STORAGE_KEY]);
    const currentTypeIndex = selectedItems.findIndex((obj: { type: string }) => obj.type === type);
    assets.forEach((asset: AssetUIModel) => {
      const itemIndex = selectedItems[currentTypeIndex].items.findIndex((item: AnyType) => item._id === asset._id);
      if (asset.isAddedToBasket && itemIndex === -1) {
        selectedItems[currentTypeIndex].items.push(asset);
      } else {
        const itemsFiltered = selectedItems[currentTypeIndex].items.filter((item: AnyType) => {
          return item._id !== asset._id;
        });
        selectedItems[currentTypeIndex].items = itemsFiltered;
      }
    });

    if (selectedItems[currentTypeIndex].selectedAll === true) {
      selectedItems[currentTypeIndex].selectedAll = false;
    }

    sessionStorage.setItem(AppSettings.SESSION_STORAGE_KEY, JSON.stringify(selectedItems));
  }

  selectAllAccessibility() {
    return (
      this.canPipe.transform(this.AnyAsset, 'delete') ||
      this.canPipe.transform(this.AnyAsset, 'download') ||
      this.canPipe.transform(this.AnyAsset, 'share') ||
      this.canPipe.transform(this.AnyAsset, 'basket.access') ||
      this.canPipe.transform(this.AnyAsset, 'collection.create')
    );
  }

  async exportDownloads() {
    const assets = await this.getAssets();
    this.dialog.open(BulkExportDownloadsComponent, { width: '100%', maxWidth: '50rem', data: assets });
  }

  exportAssets() {
    this.assetCommonService.exportAssetInfo.next(true);
  }
}
